// components/WorkerOnboarding/WorkerTable.jsx

import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import LastCallStatusModal from "./LastCallStatusModal";

// ─── Date formatters ──────────────────────────────────────────────────────────

const formatDisplayDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDisplayDateTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const safe = (val) => (val != null && val !== "" ? val : "—");

// ─── Portal Tooltip ───────────────────────────────────────────────────────────

const TooltipPortal = ({ anchorRef, lines, visible }) => {
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!visible || !anchorRef?.current) return;

    const updatePosition = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      const tooltipEl = tooltipRef.current;
      const tooltipHeight = tooltipEl ? tooltipEl.offsetHeight : 0;
      const tooltipWidth = tooltipEl ? tooltipEl.offsetWidth : 0;

      let top = rect.top + window.scrollY - tooltipHeight - 8;
      let left = rect.left + window.scrollX + rect.width / 2 - tooltipWidth / 2;

      const margin = 8;
      if (left < margin) left = margin;
      if (left + tooltipWidth > window.innerWidth - margin) left = window.innerWidth - tooltipWidth - margin;
      if (top < window.scrollY + margin) top = rect.bottom + window.scrollY + 8;

      setCoords({ top, left });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible, anchorRef]);

  if (!visible) return null;
  const filteredLines = (lines ?? []).filter(Boolean);
  if (filteredLines.length === 0) return null;

  return ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      style={{ position: "absolute", top: coords.top, left: coords.left, zIndex: 99999, pointerEvents: "none" }}
      className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-2xl whitespace-nowrap border border-gray-700"
    >
      {filteredLines.map((line, i) => (
        <div key={i} className="leading-5">{line}</div>
      ))}
      <div
        className="absolute left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"
        style={{ top: "100%" }}
      />
    </div>,
    document.body
  );
};

// ─── Tooltip Wrapper ──────────────────────────────────────────────────────────

const WithTooltip = ({ children, lines = [] }) => {
  const [visible, setVisible] = useState(false);
  const anchorRef = useRef(null);
  const filteredLines = (lines ?? []).filter(Boolean);
  const hasContent = filteredLines.length > 0;

  return (
    <div
      ref={anchorRef}
      className="relative inline-flex items-center gap-1 cursor-default"
      onMouseEnter={() => hasContent && setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {hasContent && <TooltipPortal anchorRef={anchorRef} lines={filteredLines} visible={visible} />}
    </div>
  );
};

// ─── Badge: RefCheck ──────────────────────────────────────────────────────────

const RefStatusBadge = ({ refCheck }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);

  let isChecked = null;
  let checkedAt = null;
  let doneBy = null;

  if (refCheck && typeof refCheck === "object") {
    isChecked = refCheck.isReferenceChecked;
    checkedAt = refCheck.checkedAt;
    doneBy = refCheck.doneBy ?? null;
  } else if (typeof refCheck === "boolean") {
    isChecked = refCheck;
  }

  const label =
    isChecked === true ? "Done" :
    isChecked === false ? "Pending" :
    "Pending";

  const colorClass =
    isChecked === true
      ? "bg-green-100 text-green-700"
      : "bg-gray-100 text-gray-500";

  const tooltipLines = [
    doneBy ? `Done by: ${doneBy}` : null,
    checkedAt ? `Checked on: ${formatDisplayDate(checkedAt)}` : null,
  ].filter(Boolean);

  const hasTooltip = tooltipLines.length > 0;

  return (
    <div
      ref={anchorRef}
      className="relative inline-flex items-center gap-1 cursor-default"
      onMouseEnter={() => hasTooltip && setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>{label}</span>
      {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
    </div>
  );
};

// ─── Badge: Aadhaar Verified ──────────────────────────────────────────────────

const AadhaarBadge = ({ value }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);

  let isVerified = null;
  let doneBy = null;
  let doneAt = null;

  if (value && typeof value === "object") {
    isVerified = value.isVerified ?? value.isAadhaarVerified ?? null;
    doneBy = value.doneBy ?? null;
    doneAt = value.doneAt ?? null;
  } else {
    isVerified = value;
  }

  const tooltipLines = [
    doneBy ? `Done by: ${doneBy}` : null,
    doneAt ? `Done on: ${formatDisplayDate(doneAt)}` : null,
  ].filter(Boolean);

  const hasTooltip = tooltipLines.length > 0;

  if (isVerified === true) {
    return (
      <div
        ref={anchorRef}
        className="relative inline-flex items-center gap-1 cursor-default"
        onMouseEnter={() => hasTooltip && setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        <span className="text-green-600 font-medium">Yes</span>
        {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
      </div>
    );
  }
  if (isVerified === false) {
    return (
      <div
        ref={anchorRef}
        className="relative inline-flex items-center gap-1 cursor-default"
        onMouseEnter={() => hasTooltip && setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        <span className="text-red-600 font-medium">No</span>
        {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
      </div>
    );
  }
  return <span className="text-gray-400">—</span>;
};

// ─── Badge: KYC ──────────────────────────────────────────────────────────────

const KycBadge = ({ value }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);

  let isDone = null;
  let doneBy = null;
  let doneAt = null;

  if (value && typeof value === "object") {
    isDone = value.isDone ?? value.isKyc ?? null;
    doneBy = value.doneBy ?? null;
    doneAt = value.doneAt ?? null;
  } else {
    isDone = value;
  }

  const tooltipLines = [
    doneBy ? `Done by: ${doneBy}` : null,
    doneAt ? `Done on: ${formatDisplayDate(doneAt)}` : null,
  ].filter(Boolean);

  const hasTooltip = tooltipLines.length > 0;

  if (isDone === true) {
    return (
      <div
        ref={anchorRef}
        className="relative inline-flex items-center gap-1 cursor-default"
        onMouseEnter={() => hasTooltip && setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        <span className="text-green-600 font-medium">Yes</span>
        {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
      </div>
    );
  }
  if (isDone === false) {
    return (
      <div
        ref={anchorRef}
        className="relative inline-flex items-center gap-1 cursor-default"
        onMouseEnter={() => hasTooltip && setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        <span className="text-red-600 font-medium">No</span>
        {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
      </div>
    );
  }
  return <span className="text-gray-400">—</span>;
};

// ─── Badge: Blacklisted ───────────────────────────────────────────────────────

const BlacklistedBadge = ({ blacklist }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);

  let isBlacklisted = null;
  let reason = null;
  let markedAt = null;
  let markedBy = null;

  if (blacklist && typeof blacklist === "object") {
    isBlacklisted = blacklist.isBlacklisted;
    reason = blacklist.reason ?? null;
    markedAt = blacklist.markedAt ?? null;
    markedBy = blacklist.markedBy ?? null;
  } else if (typeof blacklist === "boolean") {
    isBlacklisted = blacklist;
  }

  if (isBlacklisted === null || isBlacklisted === undefined) {
    return <span className="text-gray-400">—</span>;
  }

  if (!isBlacklisted) {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">No</span>;
  }

  const tooltipLines = [
    reason ? `Reason: ${reason}` : null,
    markedBy ? `Marked by: ${markedBy}` : null,
    markedAt ? `Marked on: ${formatDisplayDate(markedAt)}` : null,
  ].filter(Boolean);

  return (
    <div
      ref={anchorRef}
      className="relative inline-flex items-center gap-1 cursor-default"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Yes</span>
      {tooltipLines.length > 0 && (
        <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />
      )}
    </div>
  );
};

// ─── Badge: eShram ────────────────────────────────────────────────────────────

const EShramBadge = ({ eshram }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const hasUAN = eshram?.UAN != null && eshram.UAN !== "";

  if (!hasUAN) {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">—</span>;
  }

  const tooltipLines = [
    `UAN: ${eshram.UAN}`,
    eshram.doneBy ? `Done by: ${eshram.doneBy}` : null,
    eshram.doneAt ? `Done on: ${formatDisplayDate(eshram.doneAt)}` : null,
  ].filter(Boolean);

  return (
    <div
      ref={anchorRef}
      className="relative inline-flex items-center gap-1 cursor-default"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700">Done</span>
      {tooltipLines.length > 0 && (
        <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />
      )}
    </div>
  );
};

const AccountVerificationBadge = ({ nameVerification }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const status = nameVerification?.status;

  const tooltipLines = [
  nameVerification?.doneBy ? `Done by: ${nameVerification.doneBy}` : null,
  nameVerification?.doneAt ? `Done on: ${formatDisplayDate(nameVerification.doneAt)}` : null,
  nameVerification?.isBankNameException ? `Bank exception: Yes` : null,
  nameVerification?.bankExceptionReason ? `Reason: ${nameVerification.bankExceptionReason}` : null,
  nameVerification?.rejectionReason ? `Rejection reason: ${nameVerification.rejectionReason}` : null,
].filter(Boolean);

  const hasTooltip = tooltipLines.length > 0;

  if (status === "success") {
    return (
      <div
        ref={anchorRef}
        className="relative inline-flex items-center gap-1 cursor-default"
        onMouseEnter={() => hasTooltip && setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        <span className="text-green-600 font-medium">Yes</span>
        {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div
        ref={anchorRef}
        className="relative inline-flex items-center gap-1 cursor-default"
        onMouseEnter={() => hasTooltip && setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        <span className="text-red-600 font-medium">No</span>
        {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
      </div>
    );
  }

  // "not checked" or null/undefined
  return <span className="text-gray-400">—</span>;
};

const PoliceVerificationBadge = ({ policeVerification }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // If no data, show dash
  if (!policeVerification) {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">—</span>;
  }

  let status = null;
  let doneBy = null;
  let doneAt = null;

  // Handle different data structures
  if (typeof policeVerification === 'object') {
    // Check if it has a status property
    status = policeVerification.status || null;
    
    // If no status, check for other properties
    if (!status) {
      // Check if isVerified exists
      if (policeVerification.isVerified === true || policeVerification.verified === true) {
        status = 'verified';
      } else if (policeVerification.isVerified === false || policeVerification.verified === false) {
        status = 'failed';
      } else if (policeVerification.uploadedAt) {
        status = 'uploaded';
      }
    }
    
    doneBy = policeVerification.verifiedBy || policeVerification.doneBy || policeVerification.uploadedBy || null;
    doneAt = policeVerification.verifiedAt || policeVerification.doneAt || policeVerification.uploadedAt || null;
  } else if (typeof policeVerification === 'string') {
    // If it's a string, use it directly
    status = policeVerification;
  } else {
    // If it's a boolean or other type
    if (policeVerification === true) status = 'verified';
    else if (policeVerification === false) status = 'failed';
    else status = String(policeVerification);
  }

  // If status is null or undefined, show dash
  if (!status || status === "All") {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">—</span>;
  }

  // Map status to display label and color
  const statusStr = typeof status === 'string' ? status.toLowerCase() : String(status).toLowerCase();
  
  const statusMap = {
    "verified": { label: "Verified", color: "bg-green-100 text-green-700" },
    "failed": { label: "Failed", color: "bg-red-100 text-red-700" },
    "uploaded": { label: "Uploaded", color: "bg-yellow-100 text-yellow-700" },
    "not verified": { label: "Not Verified", color: "bg-yellow-100 text-yellow-700" },
    "not uploaded": { label: "Not Uploaded", color: "bg-gray-100 text-gray-500" },
    "pass": { label: "Pass", color: "bg-green-100 text-green-700" },
    "done": { label: "Done", color: "bg-blue-100 text-blue-700" },
  };

  // Default for any other status
  const display = statusMap[statusStr] || { 
    label: statusStr || "—", 
    color: "bg-gray-100 text-gray-500" 
  };

  const tooltipLines = [
    doneBy ? `Done by: ${doneBy}` : null,
    doneAt ? `Done on: ${formatDisplayDate(doneAt)}` : null,
  ].filter(Boolean);

  const hasTooltip = tooltipLines.length > 0;

  return (
    <div
      ref={anchorRef}
      className="relative inline-flex items-center gap-1 cursor-default"
      onMouseEnter={() => hasTooltip && setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${display.color}`}>
        {display.label}
      </span>
      {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
    </div>
  );
};

const MarksheetBadge = ({ marksheet }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // If no data, show dash
  if (!marksheet) {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">—</span>;
  }

  let status = null;
  let doneBy = null;
  let doneAt = null;

  // Handle different data structures
  if (typeof marksheet === 'object') {
    // Check if it has a status property
    status = marksheet.status || null;
    
    // If no status, check for other properties
    if (!status) {
      // Check if isVerified exists
      if (marksheet.isVerified === true) {
        status = 'verified';
      } else if (marksheet.isVerified === false) {
        status = 'failed';
      } else if (marksheet.uploadedAt) {
        status = 'uploaded';
      }
    }
    
    doneBy = marksheet.verifiedBy || marksheet.doneBy || marksheet.uploadedBy || null;
    doneAt = marksheet.verifiedAt || marksheet.doneAt || marksheet.uploadedAt || null;
  } else if (typeof marksheet === 'string') {
    // If it's a string, use it directly
    status = marksheet;
  } else {
    // If it's a boolean or other type
    if (marksheet === true) status = 'verified';
    else if (marksheet === false) status = 'failed';
    else status = String(marksheet);
  }

  // If status is null or undefined, show dash
  if (!status || status === "All") {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">—</span>;
  }

  // Map status to display label and color
  const statusStr = typeof status === 'string' ? status.toLowerCase() : String(status).toLowerCase();

  const statusMap = {
    "verified": { label: "Verified", color: "bg-green-100 text-green-700" },
    "failed": { label: "Failed", color: "bg-red-100 text-red-700" },
    "uploaded": { label: "Uploaded", color: "bg-yellow-100 text-yellow-700" },
    "not verified": { label: "Not Verified", color: "bg-yellow-100 text-yellow-700" },
    "not uploaded": { label: "Not Uploaded", color: "bg-gray-100 text-gray-500" },
    "pass": { label: "Pass", color: "bg-green-100 text-green-700" },
    "done": { label: "Done", color: "bg-blue-100 text-blue-700" },
  };

  const display = statusMap[statusStr] || { 
    label: statusStr || "—", 
    color: "bg-gray-100 text-gray-500" 
  };

  const tooltipLines = [
    doneBy ? `Done by: ${doneBy}` : null,
    doneAt ? `Done on: ${formatDisplayDate(doneAt)}` : null,
  ].filter(Boolean);

  const hasTooltip = tooltipLines.length > 0;

  return (
    <div
      ref={anchorRef}
      className="relative inline-flex items-center gap-1 cursor-default"
      onMouseEnter={() => hasTooltip && setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${display.color}`}>
        {display.label}
      </span>
      {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
    </div>
  );
};

// ─── Badge: Company Check ─────────────────────────────────────────────────────

const CompanyCheckBadge = ({ companyCheck, isBlacklisted }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);

  if (isBlacklisted) {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Blacklisted</span>
    );
  }

  let isCleared = null;
  let checkedBy = null;
  let checkedAt = null;

  if (companyCheck && typeof companyCheck === "object") {
    isCleared = companyCheck.isChecked ?? companyCheck.companyCheck ?? null;
    checkedBy = companyCheck.checkedBy ?? null;
    checkedAt = companyCheck.checkedAt ?? null;
  } else {
    isCleared = companyCheck;
  }

  const tooltipLines = [
    checkedBy ? `Checked by: ${checkedBy}` : null,
    checkedAt ? `Checked on: ${formatDisplayDate(checkedAt)}` : null,
  ].filter(Boolean);

  const hasTooltip = tooltipLines.length > 0;

  if (isCleared) {
    return (
      <div
        ref={anchorRef}
        className="relative inline-flex items-center gap-1 cursor-default"
        onMouseEnter={() => hasTooltip && setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Employed</span>
        {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
      </div>
    );
  }

  return (
    <div
      ref={anchorRef}
      className="relative inline-flex items-center gap-1 cursor-default"
      onMouseEnter={() => hasTooltip && setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">Not Employed</span>
      {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
    </div>
  );
};

// ─── Badge: Last Call Status ──────────────────────────────────────────────────

const LastCallStatusBadge = ({ lastStatus }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const status = lastStatus?.status;
  const hasStatus = status != null && status !== "";

  let colorClass = "bg-gray-100 text-gray-500";
  if (hasStatus) {
    const sl = status.toLowerCase();
    if (sl === "completed") colorClass = "bg-green-100 text-green-700";
    else if (sl === "pending") colorClass = "bg-yellow-100 text-yellow-700";
    else if (sl === "rescheduled") colorClass = "bg-blue-100 text-blue-700";
    else if (sl === "not applicable") colorClass = "bg-gray-100 text-gray-700";
    else colorClass = "bg-indigo-100 text-indigo-700";
  }

  const tooltipLines = [
    lastStatus?.remarks ? `Remarks: ${lastStatus.remarks}` : null,
    lastStatus?.updatedBy ? `Updated by: ${lastStatus.updatedBy}` : null,
    lastStatus?.updatedAt ? `Updated at: ${formatDisplayDateTime(lastStatus.updatedAt)}` : null,
  ].filter(Boolean);

  const hasTooltip = tooltipLines.length > 0;

  return (
    <div
      ref={anchorRef}
      className="relative inline-flex items-center gap-1 cursor-default"
      onMouseEnter={() => hasTooltip && setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
        {hasStatus ? status : "—"}
      </span>
      {hasTooltip && <TooltipPortal anchorRef={anchorRef} lines={tooltipLines} visible={visible} />}
    </div>
  );
};

// ─── Helper: open worker in new tab ──────────────────────────────────────────

const openWorkerInNewTab = (workerId) => {
  window.open(
    `/lkart/superadmin/attendance-history/${workerId}`,
    "_blank",
    "noopener,noreferrer"
  );
};

// ─── Main Table Component ─────────────────────────────────────────────────────

export const WorkerTable = ({
  workers,
  currentPage,
  pageSize,
  totalPages,
  setCurrentPage,
  loading,
  showCompanyCheck = false,
  onUpdateLastCallStatus,
  lastCallStatusOptions = [],
   lastCallSubStatusOptions = [],
  fetchLastCallStatuses,
  fetchLastCallSubStatuses,
  updateWorkerVerificationStatus,
}) => {
  // useNavigate intentionally removed — navigation now uses window.open
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const safeWorkers = Array.isArray(workers) ? workers : [];

  const handleEditClick = (e, worker) => {
    e.stopPropagation();
    setSelectedWorker(worker);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedWorker(null);
  };

  const handleStatusUpdate = (workerId, newStatus, remarks) => {
    if (onUpdateLastCallStatus) {
      onUpdateLastCallStatus(workerId, newStatus, remarks);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading workers...
      </div>
    );
  }

  return (
    <>
      {safeWorkers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No workers found with the selected filters.
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto mt-2">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Worker Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Worker Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aadhaar Verified</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">KYC</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref Check</th>
                  {showCompanyCheck && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company Check</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blacklisted</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">eShram</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Police Verification</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Educational Certificate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Verification</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Call Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {safeWorkers.map((w, index) => {
                  const nameTipLines = [
                    w.lastCompanyId && w.lastCompanyName
                      ? `Last Company: ${w.lastCompanyName}`
                      : w.lastCompanyId ? `Last Company ID: ${w.lastCompanyId}` : "Last Company: —",
                    w.lastWorkedDate
                      ? `Last Worked On: ${formatDisplayDate(w.lastWorkedDate)}`
                      : "Last Worked On: —",
                    w.registeredAt
                      ? `Registered On: ${formatDisplayDate(w.registeredAt)}`
                      : null,
                  ].filter(Boolean);

                  const serialNumber = (currentPage - 1) * pageSize + index + 1;

                  return (
                    <tr
                      key={w.workerId ?? index}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => openWorkerInNewTab(w.workerId)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-500">{serialNumber}</td>

                      <td className="px-4 py-3 text-sm">
                        <WithTooltip lines={nameTipLines}>
                          <div>
                            <div className="font-medium text-gray-900 capitalize">{safe(w.name)}</div>
                            <div className="text-xs text-gray-400 truncate max-w-[200px]">
                              {safe(w.preferredLocation)} · {safe(w.source)}
                            </div>
                          </div>
                        </WithTooltip>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{safe(w.workerCode)}</td>

                      <td className="px-4 py-3 text-sm text-gray-700">{safe(w.phone)}</td>

                      <td className="px-4 py-3 text-sm">
                        <AadhaarBadge value={w.isAadhaarVerified} />
                      </td>

                      <td className="px-4 py-3 text-sm">
                        <KycBadge value={w.isKyc} />
                      </td>

                      <td className="px-4 py-3 text-sm">
                        <RefStatusBadge refCheck={w.refCheck} />
                      </td>

                      {showCompanyCheck && (
                        <td className="px-4 py-3 text-sm">
                          <CompanyCheckBadge
                            companyCheck={w.companyCheck}
                            isBlacklisted={w.blacklist?.isBlacklisted}
                          />
                        </td>
                      )}

                      <td className="px-4 py-3 text-sm">
                        <BlacklistedBadge blacklist={w.blacklist} />
                      </td>

                      <td className="px-4 py-3 text-sm">
                        <EShramBadge eshram={w.eshram} />
                      </td>

                      <td className="px-4 py-3 text-sm">
                        <PoliceVerificationBadge policeVerification={w.policeVerification} />
                      </td>

                      <td className="px-4 py-3 text-sm">
                        <MarksheetBadge marksheet={w.marksheet} />
                      </td>

                      <td className="px-4 py-3 text-sm">
                        <AccountVerificationBadge nameVerification={w.nameVerification} />
                      </td>

                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <LastCallStatusBadge lastStatus={w.lastStatus} />
                          <button
                            onClick={(e) => handleEditClick(e, w)}
                            className="text-indigo-600 hover:text-indigo-900 focus:outline-none transition-colors"
                            title="Edit last call status"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4 mt-2">
            {safeWorkers.map((w, index) => {
              const serialNumber = (currentPage - 1) * pageSize + index + 1;

              return (
                <div
                  key={w.workerId ?? index}
                  className="border rounded-lg p-4 shadow-sm bg-white cursor-pointer"
                  onClick={() => openWorkerInNewTab(w.workerId)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-medium text-gray-900 capitalize">
                        {serialNumber}. {safe(w.name)}
                      </span>
                      <p className="text-xs text-gray-500">{safe(w.phone)}</p>
                    </div>
                  </div>

                  <div className="mb-2">
                    <p className="text-xs text-gray-400 truncate">
                      {safe(w.preferredLocation)} · {safe(w.source)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Last Co.: {w.lastCompanyName || "—"} ·
                      Last Worked: {w.lastWorkedDate ? formatDisplayDate(w.lastWorkedDate) : "—"} ·
                      Reg: {w.registeredAt ? formatDisplayDate(w.registeredAt) : "—"}
                    </p>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Aadhaar:</span>
                      <AadhaarBadge value={w.isAadhaarVerified} />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">KYC:</span>
                      <KycBadge value={w.isKyc} />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Ref Check:</span>
                      <RefStatusBadge refCheck={w.refCheck} />
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-gray-500">Blacklisted:</span>
                      {w.blacklist?.isBlacklisted ? (
                        <div className="text-right">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Yes</span>
                          {w.blacklist?.reason && (
                            <p className="text-xs text-red-500 mt-0.5">{w.blacklist.reason}</p>
                          )}
                          {w.blacklist?.markedAt && (
                            <p className="text-xs text-gray-400">{formatDisplayDate(w.blacklist.markedAt)}</p>
                          )}
                        </div>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">No</span>
                      )}
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-gray-500">eShram:</span>
                      {w.eshram?.UAN ? (
                        <div className="text-right">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700">Done</span>
                          <p className="text-xs text-gray-400 mt-0.5">UAN: {w.eshram.UAN}</p>
                          {w.eshram?.doneBy && (
                            <p className="text-xs text-gray-400">By: {w.eshram.doneBy}</p>
                          )}
                          {w.eshram?.doneAt && (
                            <p className="text-xs text-gray-400">{formatDisplayDate(w.eshram.doneAt)}</p>
                          )}
                        </div>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">—</span>
                      )}
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-gray-500">Police Verification:</span>
                      <PoliceVerificationBadge policeVerification={w.policeVerification} />
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-gray-500">Educational Certificate:</span>
                      <MarksheetBadge marksheet={w.marksheet} />
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Account Verification:</span>
                      <AccountVerificationBadge nameVerification={w.nameVerification} />
                    </div>

                    {showCompanyCheck && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Company Check:</span>
                        <CompanyCheckBadge
                          companyCheck={w.companyCheck}
                          isBlacklisted={w.blacklist?.isBlacklisted}
                        />
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <span className="text-gray-500">Last Call Status:</span>
                      <div className="flex items-center gap-2">
                        <LastCallStatusBadge lastStatus={w.lastStatus} />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(e, w);
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit last call status"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-4">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      <LastCallStatusModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        worker={selectedWorker}
        onUpdate={handleStatusUpdate}
        statusOptions={lastCallStatusOptions}
        fetchLastCallStatuses={fetchLastCallStatuses}
        fetchLastCallSubStatuses={fetchLastCallSubStatuses}

        updateWorkerVerificationStatus={updateWorkerVerificationStatus}
      />
    </>
  );
};